"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

import { GroqImage } from "@/helpers/Image";
import type { GROQImage } from "@/sanity/lib/definitions";
import type { PageRizAffiliateSchema } from "@/sanity/pages/PageRizAffiliateTemplate";
import { measuredRichText, V3_HIGHLIGHT } from "./richtext";
import ChevronCta from "./ChevronCta";
import CodeBox from "./CodeBox";
import { EXPO, RevealBlock, RevealEyebrow, RevealRichText, RevealStyles, RevealWords } from "./about/reveal";
import DriftRow from "./final/DriftRow";
import FinalSticker from "./final/FinalSticker";

import noise from "@/public/fabio-affiliate/noise.jpg";

const HEADING_CLASS = `${V3_HIGHLIGHT} richtext richtext-center w-full lg:w-[min(87.5rem,100%)] prose-headings:text-black prose-headings:font-geist prose-headings:font-[600] lg:prose-headings:font-[500] prose-headings:text-[5rem] prose-headings:tracking-[-0.25rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] [--hl-tracking:-0.03em] [--hl-wash:5rem] lg:[--hl-wash:6.75rem] [--hl-dy:0.125rem] lg:[--hl-dy:0.25rem] max-lg:[--hl-ctr:0px] lg:[--hl-pl:0.925rem] lg:[--hl-ml:1.5rem] [&_span]:!text-black lg:[&_span:has(>.hl-wash)]:[--hl-tex:0.8333rem] prose-headings:leading-none`;

function EpisodeRow({
  episodes,
  direction,
  offset,
}: {
  episodes: GROQImage[];
  direction: "left" | "right";
  offset: { mobile: number; desktop: number };
}) {
  return (
    <DriftRow
      direction={direction}
      offset={offset}>
      {episodes.map((episode, i) => (
        <div
          key={i}
          className="shrink-0 overflow-hidden w-[31.75rem] aspect-[359/202] mr-[1.25rem] rounded-[0.625rem] lg:w-[34rem] lg:mr-[2.5rem] lg:rounded-[0.875rem] lg:border lg:border-black/[0.08]">
          <GroqImage
            src={episode}
            alt=""
            quality={100}
            sizes="(min-width: 993px) 14.2vw, 59vw"
            style={{ width: "100%" }}
            className="!w-full !h-full object-cover"
          />
        </div>
      ))}
    </DriftRow>
  );
}

function FinalStage({ data }: { data: PageRizAffiliateSchema }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.12, margin: "0px 0px -6% 0px" });
  const stickerInView = useInView(ref, { once: true, amount: 0.35 });
  const [loaded, setLoaded] = useState(false);
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    if (!inView || waited) return;
    const timer = window.setTimeout(() => setWaited(true), 900);
    return () => window.clearTimeout(timer);
  }, [inView, waited]);

  const episodes = data?.finalCtaEpisodes?.filter(Boolean) ?? [];
  const half = Math.ceil(episodes.length / 2);
  const top = episodes.slice(0, half);
  const bottom = episodes.slice(half);
  const show = inView && (loaded || waited);

  return (
    <div
      ref={ref}
      className="relative w-full h-[49.25rem] lg:h-[65.125rem]">
      {episodes.length > 0 && (
        <div
          aria-hidden
          className="flex absolute inset-x-0 flex-col top-[8.5rem] gap-[1.25rem] lg:top-[21.25rem] lg:gap-[2.5rem]">
          <EpisodeRow
            episodes={top}
            direction="left"
            offset={{ mobile: -36.8, desktop: -152.46 }}
          />
          {bottom.length > 0 && (
            <EpisodeRow
              episodes={bottom}
              direction="right"
              offset={{ mobile: -53.75, desktop: -140.18 }}
            />
          )}
        </div>
      )}

      {data?.finalCtaImage && (
        <div className="absolute top-[4.25rem] left-[calc(50%-27.125rem)] w-[min(54.25rem,101%)] lg:top-[5.5rem] lg:left-[calc(50%-37.75rem)] lg:w-[min(77rem,100%)]">
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 1.02, filter: "blur(12px)" }}
            animate={show ? { opacity: 1, scale: 1, filter: "blur(0px)" } : undefined}
            transition={{
              opacity: { duration: 0.9, ease: "easeInOut" },
              scale: { duration: 1.2, ease: EXPO },
              filter: { duration: 1, ease: EXPO },
            }}>
            <GroqImage
              src={data.finalCtaImage}
              alt=""
              quality={100}
              sizes="(min-width: 993px) 32vw, 101vw"
              style={{ width: "100%" }}
              onLoad={() => setLoaded(true)}
              className="pointer-events-none !w-full !h-auto"
            />
          </motion.div>
        </div>
      )}

      {data?.finalCtaSticker && (
        <FinalSticker
          text={data.finalCtaSticker}
          play={stickerInView}
          reduce={reduce}
          className="absolute left-[2rem] top-[24.75rem] lg:left-[calc(50%-32.25rem)] lg:top-[9.125rem]"
        />
      )}
    </div>
  );
}

export default function FinalCTASection({ data }: { data: PageRizAffiliateSchema }) {
  if (
    !data?.finalCtaChip &&
    !data?.finalCtaHeading?.length &&
    !data?.finalCtaSubheading &&
    !data?.finalCtaButton &&
    !data?.finalCtaImage
  )
    return null;

  const hasCode = !!data?.code;

  return (
    <section className="relative overflow-hidden bg-[#B4B4B4]/[15%] pt-[8rem] lg:pt-[11.75rem]">
      <RevealStyles />
      <div
        aria-hidden
        className="absolute inset-0 opacity-10 mix-blend-exclusion pointer-events-none bg-repeat-y bg-[length:103.5rem_53.25rem] bg-[position:2rem_0] lg:bg-no-repeat lg:bg-[length:242.625rem_124.875rem] lg:bg-[position:-1.375rem_1.25rem]"
        style={{ backgroundImage: `url(${noise.src})` }}
      />

      <div className="flex relative flex-col items-center mx-auto w-[min(49.75rem,calc(100%-4rem))] gap-[2.5rem] lg:w-full">
        {(data?.finalCtaChip || data?.finalCtaHeading?.length > 0) && (
          <div className="flex flex-col items-center w-full gap-[2rem] lg:gap-[1.25rem]">
            <RevealEyebrow text={data?.finalCtaChip} />
            <RevealRichText
              value={data?.finalCtaHeading}
              components={measuredRichText}
              className={HEADING_CLASS}
            />
          </div>
        )}

        <RevealWords
          text={data?.finalCtaSubheading}
          className="m-0 mt-[0.5rem] text-center font-geist text-[2.25rem] leading-[1.5] text-black/80 w-[min(45rem,100%)] lg:mt-0 lg:w-auto"
        />

        {(data?.finalCtaButton || hasCode) && (
          <RevealBlock className="flex flex-col w-full gap-[1.5rem] lg:flex-row lg:items-center lg:w-auto lg:gap-[2rem]">
            {data?.finalCtaButton && (
              <ChevronCta
                data={data.finalCtaButton}
                language={data?.settings?.language}
              />
            )}
            {hasCode && (
              <CodeBox
                label={data.codeLabel}
                code={data.code}
                copyLabel={data.codeCopyLabel}
                className="w-full lg:w-auto"
              />
            )}
          </RevealBlock>
        )}
      </div>

      <FinalStage data={data} />
    </section>
  );
}
